import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { palette } from '@openx/components-core/lib/theme/paletteV2.ts';
import type { DealApi, DealTroubleshoot } from '@openx/types/deal.ts';
import { DealTroubleshootingTimeResolutionEnum } from '@openx/types/gqlTypes.ts';
import { generateDateRangeCaption } from '@openx/utils/lib/chartFunctions.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TroubleshootingParams } from '../../../utils/hooks/useTroubleshootingFetch.ts';
import { TroubleshootingChart } from '../../TroubleshootingChart.tsx';
import type { TimePeriod } from '../constants.ts';
import type { DateRange } from './TroubleshootingDateFilter.tsx';
import { TroubleshootingNoDataMessage } from './TroubleshootingNoDataMessage.tsx';

type DealTroubleshootingContentProps = {
  dealId: string;
  dealParticipants: DealApi['deal_participants'];
  selectedPeriod: TimePeriod;
  customDateRange?: DateRange;
  selectedDspId: string;
  troubleshoot: DealTroubleshoot[];
  error?: string | null;
  troubleshootingParams?: TroubleshootingParams;
  onRefetch?: () => void | Promise<void>;
};

export const DealTroubleshootingContent = ({
  troubleshoot,
  error,
  troubleshootingParams,
  onRefetch,
}: DealTroubleshootingContentProps) => {
  const { t } = useTranslation();

  const caption = useMemo(() => (troubleshoot ? generateDateRangeCaption(troubleshoot) : ''), [troubleshoot]);

  const hasBidCpmData = useMemo(
    () => troubleshoot?.some(item => item.stats.bid_cpm !== undefined && item.stats.bid_cpm !== null) ?? false,
    [troubleshoot]
  );

  // Determine if data is hourly (NRT) or daily (Fact) based on params
  const isHourlyData = useMemo(() => {
    return (
      !!troubleshootingParams?.lookbackHours ||
      troubleshootingParams?.resolution === DealTroubleshootingTimeResolutionEnum.Hourly
    );
  }, [troubleshootingParams]);

  const hasNoData = error || !troubleshoot || troubleshoot.length === 0;

  return (
    <Box
      data-test="troubleshooting-charts-container"
      sx={{ padding: theme => theme.spacing(0, 0, 3.5), width: '100%' }}
    >
      {!hasNoData && (
        <Box sx={{ padding: theme => theme.spacing(3, 4, 0) }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              marginBottom: 2,
            }}
          >
            {t('Time-series charts visualizing performance trends over the selected date range.')}
          </Typography>
        </Box>
      )}
      {hasNoData ? (
        <TroubleshootingNoDataMessage onReload={onRefetch} isChartsOnly={true} />
      ) : (
        <>
          <TroubleshootingChart
            title={t('Bid Requests')}
            metric={'stats.bid_requests'}
            data={troubleshoot}
            color={palette.charts.blue}
            caption={caption}
            isHourlyData={isHourlyData}
          />
          <TroubleshootingChart
            title={t('Bid Responses')}
            metric={'stats.bid_responses'}
            data={troubleshoot}
            color={palette.info.main}
            caption={caption}
            isHourlyData={isHourlyData}
          />
          <TroubleshootingChart
            title={t('Impressions')}
            metric={'stats.impressions'}
            data={troubleshoot}
            color={palette.error.main}
            caption={caption}
            isHourlyData={isHourlyData}
          />
          {hasBidCpmData ? (
            <TroubleshootingChart
              title={t('Bid CPM')}
              metric={'stats.bid_cpm'}
              data={troubleshoot}
              color={palette.charts.lightBlue}
              caption={caption}
              isHourlyData={isHourlyData}
            />
          ) : (
            <Box sx={{ padding: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary" data-test="bid-cpm-no-data-info">
                {troubleshootingParams?.lookbackHours
                  ? t('Bid CPM data is not available for NRT filters')
                  : t('Bid CPM data is not available for the selected date range')}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
